import React from "react";
import mime from "mime";
import _i18n from "../reducers/i18n";
import { Box, Typography } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import store from "../store";

let i18n = _i18n();

export function truncateWithEllipses(text, max) {
  try {
    if (!text) {
      return "";
    }
    return text.substr(0, max - 1) + (text.length > max ? "…" : "");
  } catch (e) {
    console.error(e);
  }
}

const SortArrows = ({ active, direction, id }) => {
  const base = "rgba(0,0,0,0.38)"; // grey like your mock
  const on = "var(--color-primary)"; // your heading color

  const upColor = active && direction === "asc" ? on : base;
  const downColor = active && direction === "desc" ? on : base;

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        lineHeight: 1,
        marginLeft: id === "active" ? "-39%" : "",
      }}
    >
      <KeyboardArrowUpIcon sx={{ fontSize: 18, color: upColor }} />
      <KeyboardArrowDownIcon
        sx={{ fontSize: 18, color: downColor, mt: "-6px" }}
      />
    </Box>
  );
};

export const SortHeader = ({
  id,
  orderBy,
  order,
  setOrderBy,
  setOrder,
  children,
}) => {
  const active = orderBy === id;
  const direction = active ? order : "asc";
  const handleClick = () => {
    if (active) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  return (
    <Box
      role="button"
      aria-sort={
        active ? (order === "asc" ? "ascending" : "descending") : "none"
      }
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.5,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {/* label */}
      <Typography
        component="span"
        sx={{
          color: "var(--color-primary)",
          fontFamily: "Roboto",
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: 0.15,
        }}
      >
        {children}
      </Typography>
      {/* right arrows */}
      <SortArrows active={active} direction={direction} id={id} />
    </Box>
  );
};

export const isValidEmail = (emailAdress) => {
  let regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  if (emailAdress.match(regex)) return true;
  else return false;
};

export function GetTitleSafely(notification) {
  const state = store.getState();
  const uiLanguage = state?.uiLanguage || state?.language || "de";

  // Notification titles can be either plain strings or localized objects.
  if (!notification?.title) {
    return "";
  }

  if (typeof notification.title === "string") {
    return notification.title;
  }

  return (
    notification.title?.[uiLanguage] ||
    notification.title?.de ||
    notification.title?.en ||
    ""
  );
}

export const getHighlightedText = (text, highlight) => {
  // Split on highlight term and include term into parts, ignore case
  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  /* console.log(parts,highlight) */
  return (
    <span>
      {" "}
      {parts.map((part, i) => (
        <span
          key={i}
          style={
            part?.toLowerCase() === highlight?.toLowerCase()
              ? { color: "#98BCC4" }
              : {}
          }
        >
          {part}
        </span>
      ))}{" "}
    </span>
  );
};

export const validateField = (field) => {
  return field === "";
};

export const allow_docs_mimetypes = [
  // ✅ PDF
  "application/pdf",

  // ✅ Microsoft Word (Windows & Mac)
  "application/msword", // DOC (Word 97-2003)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX (Word)

  // ✅ Microsoft Excel (Windows & Mac)
  "application/vnd.ms-excel", // XLS (Excel 97-2003)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX (Excel)

  // ✅ Microsoft PowerPoint (Windows & Mac)
  "application/vnd.ms-powerpoint", // PPT (PowerPoint 97-2003)
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX (PowerPoint)

  // ✅ Plain Text (TXT)
  "text/plain",

  // ✅ Apple iWork Files (Mac)
  "application/vnd.apple.pages", // Apple Pages
  "application/vnd.apple.numbers", // Apple Numbers
  "application/vnd.apple.keynote", // Apple Keynote
];

export const allow_images_mimetypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/tiff",
  "image/webp",
  "image/svg+xml",
  "image/x-icon",
  "image/heif",
  "image/heic",
];

export const allow_videos_mimetypes = [
  // "video/mp4",
  // "video/mpeg",
  // "video/webm",
  // "video/quicktime",
  "video/*",
];

export const docs_enum = allow_docs_mimetypes.join(",");
export const images_enum = "image/*";
export const videos_enum = "video/*";

export const getFileExtention = (type) => {
  return mime.getExtension(type);
};

export const categoryItems = [
  { id: "operation", category: "Bedienen und Einrichten", docsCount: 0 },
  { id: "setup", category: "Rüsten", docsCount: 0 },
  { id: "maintenance", category: "Wartung und Service", docsCount: 0 },
  { id: "safety", category: "Sicherheit", docsCount: 0 },
  { id: "internalinstructions", category: "Interne Anweisungen", docsCount: 0 },
];

export const sortNumericallyThenAlphabetically = (
  array,
  keys,
  finalFormRequired
) => {
  const getPreferredLanguage = () => {
    try {
      return localStorage.getItem("uiLanguage") || "de";
    } catch (error) {
      return "de";
    }
  };

  const resolveSortableValue = (value) => {
    if (value == null) {
      return "";
    }

    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }

    if (typeof value === "object") {
      const preferredLanguage = getPreferredLanguage();

      return (
        value?.[preferredLanguage] ??
        value?.de ??
        value?.en ??
        Object.values(value).find(
          (entry) => typeof entry === "string" || typeof entry === "number"
        ) ??
        ""
      );
    }

    return String(value);
  };

  if (array.length) {
    let data = array.sort((a, b) => {
      keys.split(".").map((key) => {
        // generic like array["key1"]["key2"]....
        a = a?.[key];
        b = b?.[key];
      });
      const resolvedA = resolveSortableValue(a);
      const resolvedB = resolveSortableValue(b);
      const numA = parseInt(resolvedA); // Extract leading number, or NaN if none
      const numB = parseInt(resolvedB);

      if (isNaN(numA) && isNaN(numB)) {
        // Both have no numbers, sort alphabetically
        return resolvedA.localeCompare(resolvedB);
      }
      if (isNaN(numA)) return 1; // No number goes last
      if (isNaN(numB)) return -1; // Number goes first
      return numA - numB; // Sort by numeric value
    });

    if (finalFormRequired === "object") {
      let obj = {};
      data.map((dt) => {
        obj[dt.nodeid] = dt;
      });
      return obj;
    }

    return data;
  } else {
    return array;
  }
};

export const KPI_dummy_data = [
  {
    numeric_title_1: "622",
    numeric_title_2: "h",
    _title: "MTBF",
    _description: "Zeitraum (gesamt)",
  },
  {
    numeric_title_1: "21",
    numeric_title_2: "/ 26",
    _title: "Wartungen",
    _description: "durchgeführt",
  },
  {
    numeric_title_1: "8",
    numeric_title_2: "",
    _title: "Ungelpante Stillstände",
    _description: "letzte 12 Monate",
  },
];

export const getRandomSubset = (array, maxSize) => {
  let result = [];

  array.forEach((item) => {
    if (Math.random() < 0.5) {
      result.push(item);
    }
  });

  // Ensure the result is not empty
  if (result.length === 0) {
    result.push(array[Math.floor(Math.random() * array.length)]);
  }

  // Limit the result to the desired maxSize
  return result.slice(0, maxSize);
};

export const counterBasedRepititions = [
  // {
  //   key: "",
  //   value: "",
  //   label: "Kein Zeitinterval",
  // },

  {
    key: "onetime",
    value: "",
    label: i18n.maintenance_repetition_onetime,
  },
  {
    key: "1w",
    value: "1",
    label: i18n.maintenance_repetition_oneweek,
  },
  {
    key: "2w",
    value: "2",
    label: i18n.maintenance_repetition_twoweek,
  },
  {
    key: "1m",
    value: "1",
    label: i18n.maintenance_repetition_month,
  },
  {
    key: "2m",
    value: "2",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "3m",
    value: "3",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "6m",
    value: "6",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "12m",
    value: "12",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "18m",
    value: "18",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "24m",
    value: "24",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "36m",
    value: "36",
    label: i18n.maintenance_repetition_months,
  },
  {
    key: "48m",
    value: "48",
    label: i18n.maintenance_repetition_months,
  },
];

// dummyPoolsData.js

// Dummy data adapted from your JSON, wrapped into pools → equipment → hasnotification
export const dummyPoolsData = [
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-1",
      name: { de: "Werk Győr" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-101",
            siteName: "Werk Győr",
            type: "Fräse",
            hasnotification: [
              {
                notification: {
                  created: 1711000000000,
                  type: 0,
                  title: "Spindel blockiert",
                  description: "Mechanische Blockade festgestellt",
                  nodeid: "247fn://247uptime.io/noti-101",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1712000000000,
                  type: 10,
                  title: "Kühlmittelpumpe ausgefallen",
                  description: "",
                  nodeid: "247fn://247uptime.io/noti-102",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1713000000000,
                  type: 6,
                  title: "Geplante Inspektion",
                  description: "Wartung nach 500 Betriebsstunden",
                  nodeid: "247fn://247uptime.io/noti-103",
                  unread: false,
                },
              },
            ],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-2",
      name: { de: "Werk Pforzheim" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-102",
            siteName: "Werk Pforzheim",
            type: "Drehbank",
            hasnotification: [
              {
                notification: {
                  created: 1711500000000,
                  type: 0,
                  title: "Ungewöhnliche Vibrationen",
                  description: "Operator meldet starke Vibration",
                  nodeid: "247fn://247uptime.io/noti-201",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1712500000000,
                  type: 0,
                  title: "Hydraulikdruck niedrig",
                  description: "",
                  nodeid: "247fn://247uptime.io/noti-202",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1713500000000,
                  type: 2,
                  title: "Werkzeugwechsel geplant",
                  description: "Neues Werkzeug bestellt",
                  nodeid: "247fn://247uptime.io/noti-203",
                  unread: false,
                },
              },
              {
                notification: {
                  created: 1713600000000,
                  type: 0,
                  title: "Ölwechsel",
                  description: "Wartungsteam informieren",
                  nodeid: "247fn://247uptime.io/noti-204",
                  unread: false,
                },
              },
            ],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-3",
      name: { de: "Werk München" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-103",
            siteName: "Werk München",
            type: "Roboterarm",
            hasnotification: [
              {
                notification: {
                  created: 1711700000000,
                  type: 1,
                  title: "Greifer klemmt",
                  description: "Greifer fährt nicht vollständig zurück",
                  nodeid: "247fn://247uptime.io/noti-301",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1712700000000,
                  type: 1,
                  title: "Achse Y überlastet",
                  description: "",
                  nodeid: "247fn://247uptime.io/noti-302",
                  unread: false,
                },
              },
              {
                notification: {
                  created: 1713700000000,
                  type: 1,
                  title: "Bitte Kontrolle durchführen",
                  description: "Operator meldet ungenaue Bewegung",
                  nodeid: "247fn://247uptime.io/noti-303",
                  unread: true,
                },
              },
            ],
          },
        },
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-104",
            siteName: "Werk München",
            type: "Montageroboter",
            hasnotification: [],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-4",
      name: { de: "Werk Stuttgart" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-105",
            siteName: "Werk Stuttgart",
            type: "3D Drucker",
            hasnotification: [
              {
                notification: {
                  created: 1711800000000,
                  type: 0,
                  title: "Materialnachschub erforderlich",
                  description: "PLA Filament fast leer",
                  nodeid: "247fn://247uptime.io/noti-401",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1712800000000,
                  type: 0,
                  title: "Druckkopf überhitzt",
                  description: "Temperatur > 250°C",
                  nodeid: "247fn://247uptime.io/noti-402",
                  unread: true,
                },
              },
              {
                notification: {
                  created: 1713800000000,
                  type: 3,
                  title: "Kalibrierung notwendig",
                  description: "Druckbett muss neu kalibriert werden",
                  nodeid: "247fn://247uptime.io/noti-403",
                  unread: false,
                },
              },
              {
                notification: {
                  created: 1713900000000,
                  type: 0,
                  title: "Fehler im Extruder",
                  description: "Extruder blockiert",
                  nodeid: "247fn://247uptime.io/noti-404",
                  unread: true,
                },
              },
            ],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-5",
      name: { de: "Werk Hamburg" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-106",
            siteName: "Werk Hamburg",
            type: "Laser Cutter",
            hasnotification: [
              {
                notification: {
                  created: 1712100000000,
                  type: 9,
                  title: "Verbesserungsvorschlag",
                  description: "Neue Schneidparameter testen",
                  nodeid: "247fn://247uptime.io/noti-501",
                  unread: false,
                },
              },
              {
                notification: {
                  created: 1712200000000,
                  type: 0,
                  title: "Laserleistung schwankt",
                  description: "",
                  nodeid: "247fn://247uptime.io/noti-502",
                  unread: true,
                },
              },
            ],
          },
        },
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-107",
            siteName: "Werk Hamburg",
            type: "CNC Maschine",
            hasnotification: [],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-6",
      name: { de: "Werk Köln" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-108",
            siteName: "Werk Köln",
            type: "Bohrmaschine",
            hasnotification: [
              {
                notification: {
                  created: 1712300000000,
                  type: 0,
                  title: "Aufgabe: Werkzeugwechsel",
                  description: "Bohrer stumpf, Ersatz einsetzen",
                  nodeid: "247fn://247uptime.io/noti-601",
                  unread: true,
                },
              },
            ],
          },
        },
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-109",
            siteName: "Werk Köln",
            type: "Hydraulikpresse",
            hasnotification: [
              {
                notification: {
                  created: 1712400000000,
                  type: 0,
                  title: "Reaktive Wartung",
                  description: "Hydrauliköl austreten",
                  nodeid: "247fn://247uptime.io/noti-602",
                  unread: false,
                },
              },
            ],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-7",
      name: { de: "Werk Leipzig" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-110",
            siteName: "Werk Leipzig",
            type: "Montagelinie",
            hasnotification: [],
          },
        },
      ],
    },
  },
  {
    equipmentpool: {
      nodeid: "247fn://247uptime.io/pool-8",
      name: { de: "Werk Hannover" },
      hasequipment: [
        {
          equipment: {
            nodeid: "247fn://247uptime.io/eq-111",
            siteName: "Werk Hannover",
            type: "Schweißroboter",
            hasnotification: [
              {
                notification: {
                  created: 1712500000000,
                  type: 0,
                  title: "Herstellerhinweis",
                  description: "Softwareupdate verfügbar",
                  nodeid: "247fn://247uptime.io/noti-801",
                  unread: true,
                },
              },
            ],
          },
        },
      ],
    },
  },
];



// flatten helper
export default function flatEquip(pools) {
  const out = [];
  pools?.forEach((p) =>
    p?.equipmentpool?.hasequipment?.forEach((x) => {
      if (x?.equipment) out.push(x.equipment);
    })
  );
  return out;
}


// helper function
export function getNotificationLabel(typeCode) {
  const uiLanguage = store.getState()?.uiLanguage || "de";

  const labels = {
    de: {
      0: "Stillstand",
      1: "Auffälligkeit",
      2: "Notiz",
      3: "Aufgabe",
      4: "Interne Mitteilung",
      5: "Herstellermitteilung",
      6: "Geplant Wartung",
      7: "Reaktive Wartung",
      8: "Instandsetzung",
      9: "Verbesserung",
      10: "Ungeplanter Stillstand (Verschleiß)",
      11: "Ungeplanter Stillstand (Bedienungsfehler)",
      12: "Störung (ohne Stillstand)",
    },
    en: {
      0: "Downtime",
      1: "Anomaly",
      2: "Note",
      3: "Task",
      4: "Internal message",
      5: "Manufacturer message",
      6: "Planned Maintenance",
      7: "Reactive Maintenance",
      8: "Repair",
      9: "Improvement",
      10: "Unplanned downtime (wear)",
      11: "Unplanned downtime (operating error)",
      12: "Fault (without downtime)",
    },
  };

  return labels[uiLanguage]?.[typeCode] || labels.de?.[typeCode] || "Unbekannt";
}

// Extended fallback demo users (15 total)
export const USERS_FALLBACK = [
  {
    firstName: "Susanne",
    lastName: "Laufner",
    email: "susanne.laufner@example.com",
    mobile: "+49 171 2345678",
    landline: "+49 711 123456",
    status: "aktiv",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Susanne%20Laufner&backgroundType=gradientLinear",
  },
  {
    firstName: "Max",
    lastName: "Keller",
    email: "max.keller@example.com",
    mobile: "+49 171 9988776",
    landline: "",
    status: "deaktiviert",
    avatarUrl: "", // shows initials "MK"
  },
  {
    firstName: "Robert",
    lastName: "Schuster",
    email: "robert.schuster@example.com",
    mobile: "+49 176 1223456",
    landline: "+49 711 223344",
    status: "aktiv",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Robert%20Schuster&backgroundType=gradientLinear",
  },
  {
    firstName: "Julia",
    lastName: "Meier",
    email: "julia.meier@example.com",
    mobile: "+49 170 8887777",
    landline: "",
    status: "aktiv",
    avatarUrl: "", // shows initials "JM"
  },
  {
    firstName: "Thomas",
    lastName: "Weber",
    email: "thomas.weber@example.com",
    mobile: "+49 171 3332221",
    landline: "+49 89 111111",
    status: "deaktiviert",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Thomas%20Weber&backgroundType=gradientLinear",
  },
  {
    firstName: "Elena",
    lastName: "Schmidt",
    email: "elena.schmidt@example.com",
    mobile: "+49 151 5556667",
    landline: "",
    status: "aktiv",
    avatarUrl: "", // shows initials "ES"
  },
  {
    firstName: "Patrick",
    lastName: "Hoffmann",
    email: "patrick.hoffmann@example.com",
    mobile: "+49 152 1122334",
    landline: "+49 40 987654",
    status: "aktiv",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Patrick%20Hoffmann&backgroundType=gradientLinear",
  },
  {
    firstName: "Lisa",
    lastName: "Bauer",
    email: "lisa.bauer@example.com",
    mobile: "+49 160 5557771",
    landline: "",
    status: "aktiv",
    avatarUrl: "", // shows initials "LB"
  },
  {
    firstName: "Martin",
    lastName: "Krüger",
    email: "martin.krueger@example.com",
    mobile: "+49 171 1112223",
    landline: "+49 30 987654",
    status: "deaktiviert",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Martin%20Krueger&backgroundType=gradientLinear",
  },
  {
    firstName: "Hannah",
    lastName: "Fischer",
    email: "hannah.fischer@example.com",
    mobile: "+49 170 3334445",
    landline: "",
    status: "aktiv",
    avatarUrl: "", // shows initials "HF"
  },
  {
    firstName: "Daniel",
    lastName: "Zimmermann",
    email: "daniel.zimmermann@example.com",
    mobile: "+49 175 7778889",
    landline: "+49 221 334455",
    status: "aktiv",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Daniel%20Zimmermann&backgroundType=gradientLinear",
  },
  {
    firstName: "Nina",
    lastName: "Walter",
    email: "nina.walter@example.com",
    mobile: "+49 172 2223334",
    landline: "",
    status: "deaktiviert",
    avatarUrl: "", // shows initials "NW"
  },
  {
    firstName: "Philipp",
    lastName: "Graf",
    email: "philipp.graf@example.com",
    mobile: "+49 176 5559992",
    landline: "+49 89 998877",
    status: "aktiv",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Philipp%20Graf&backgroundType=gradientLinear",
  },
  {
    firstName: "Miriam",
    lastName: "Becker",
    email: "miriam.becker@example.com",
    mobile: "+49 173 6667771",
    landline: "",
    status: "aktiv",
    avatarUrl: "", // shows initials "MB"
  },
  {
    firstName: "Jonas",
    lastName: "Schäfer",
    email: "jonas.schaefer@example.com",
    mobile: "+49 174 8889993",
    landline: "+49 69 123456",
    status: "deaktiviert",
    avatarUrl:
      "https://api.dicebear.com/8.x/initials/svg?seed=Jonas%20Schaefer&backgroundType=gradientLinear",
  },
];
